/* eslint-disable max-len */
import { Company, UserType } from '@centecomm/portal-service';
import { useUsers } from '@centecomm/portal-service-react';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ApplicationSubscription } from './ApplicationSubscription';
import { StorageKeys } from './StorageKeys';
import { AuthProvider } from 'Core/Types/AuthProviders';
import { clearLegacyAuth0Session } from './legacyAuth0Session';

type UseSubscriptionHook = () => {
    email: string | undefined;
    organization: string | undefined;
    subscription: ApplicationSubscription;
    invitation: string | undefined;
    authProvider: string;
    logoutUser: (redirectTarget: string) => void;
    logoutAuth0User: (redirectTarget: string) => void;
    clearUser: () => void;
    getOrganization: () => string | null;
    getOrganizationLogoUrl: () => string | null;
    setOrganization: (org: string) => void;
    setOrganizationLogo: (logoUrl: string) => void;
    getUserOrganization: (email: string) => Promise<string | Company[] | null>;
    setEmail: (email: string) => void;
};

const base = {
    audience: 'https://api.centecomm.com',
    redirectUri: BASE_PATH,
};

const target = (authTarget: typeof AUTH_TARGET): ApplicationSubscription =>
    ({
        local: {
            ...base,
            authRoot: 'auth.qa.centecomm.com',
            domain: 'auth.qa.centecomm.com',
            clientId: 'ZhENQlBg5PGTx1J1Gt8Trkgoj52vAEjf',
        },
        dev: {
            ...base,
            authRoot: 'auth.qa.centecomm.com',
            domain: 'auth.qa.centecomm.com',
            clientId: 'ZhENQlBg5PGTx1J1Gt8Trkgoj52vAEjf',
        },
        prod: {
            ...base,
            authRoot: 'auth.centecomm.com',
            domain: 'auth.centecomm.com',
            clientId: 'JW2ut9Dvs4R9PnGwoou41uHxHUpfm2ex',
        },
    }[authTarget]);

const useSubscription: UseSubscriptionHook = () => {
    const { search } = useLocation();

    const [authProvider] = useState<string | undefined>(localStorage.getItem(StorageKeys.AuthProvider) || undefined);

    const subscription = target(AUTH_TARGET);

    const queryParams = new URLSearchParams(search);

    const { getUserLoginByEmail } = useUsers();

    const invitation: string | undefined = queryParams.get('invitation') || undefined;

    const [userType] = useState<string | undefined>(localStorage.getItem(StorageKeys.Type) || undefined);
    const [organization] = useState<string | undefined>(
        queryParams.get('organization') || localStorage.getItem(StorageKeys.Organization) || undefined,
    );
    const [userEmail] = useState<string | undefined>(localStorage.getItem(StorageKeys.Email) || undefined);

    const getUserOrganization = async (email: string): Promise<string | Company[] | null> => {
        const { tenant, ownerOperator, authenticationProvider } = await getUserLoginByEmail(email);

        setAuthProvider(authenticationProvider);

        if (tenant) return tenant.orgId;

        if (ownerOperator) {
            const { companies } = ownerOperator;

            return companies;
        }

        return null;
    };

    /**
     * Logout function to call from anywhere in the application regardless of auth0 provider being available
     * Will redirect to redirectTarget, default logout.html, following clearing auth session with auth0
     *
     * @param redirectTarget string - url target with no preceding slash
     */
    const logoutUser = (redirectTarget = 'logout.html') => {
        if (authProvider === AuthProvider.Clerk) {
            clearUser();
            return;
        }

        logoutAuth0User(redirectTarget);
    };

    // A migrated user's backend provider can already be Clerk while the browser
    // still holds Auth0 state. This path must always end the Auth0 server session.
    const logoutAuth0User = (redirectTarget = 'logout.html') => {
        if (typeof window !== 'undefined') {
            clearLegacyAuth0Session(subscription.clientId);
            const returnTo = encodeURIComponent(`${BASE_PATH}/${redirectTarget}`);
            window.location.replace(
                `https://${subscription.authRoot}/v2/logout?returnTo=${returnTo}&client_id=${subscription.clientId}`,
            );
        }
    };

    const clearUser = () => {
        if (typeof window !== 'undefined') {
            localStorage.clear();
        }
    };

    const getOrganizationId = (): string | null => {
        if (typeof window !== 'undefined') return localStorage.getItem(StorageKeys.Organization);

        return null;
    };

    const getOrganizationLogo = (): string | null => {
        if (typeof window !== 'undefined') return localStorage.getItem(StorageKeys.Logo);

        return null;
    };

    const setOrganization = (org: string) => {
        if (typeof window !== 'undefined') localStorage.setItem(StorageKeys.Organization, org);
    };

    const setOrganizationLogo = (logoUrl: string) => {
        if (typeof window !== 'undefined') localStorage.setItem(StorageKeys.Logo, logoUrl);
    };

    const setEmail = (email: string) => {
        if (typeof window !== 'undefined') localStorage.setItem(StorageKeys.Email, email);
    };

    const setAuthProvider = (provider: string) => {
        if (typeof window !== 'undefined') localStorage.setItem(StorageKeys.AuthProvider, provider);
    };

    return {
        baseRoute: userType && userType === UserType.OwnerOperator ? 'company' : 'tenant',
        email: userEmail,
        organization,
        subscription,
        invitation,
        authProvider,
        logoutUser,
        logoutAuth0User,
        clearUser,
        getOrganization: getOrganizationId,
        getOrganizationLogoUrl: getOrganizationLogo,
        setOrganization,
        setOrganizationLogo,
        getUserOrganization,
        setEmail,
        setAuthProvider,
    };
};
export default useSubscription;
